import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "../lib/supabase";

/**
 * Fires the extract-metadata Edge Function on a case's operative filing.
 * Fast (~10-20s, single OpenAI call, no paragraph extraction). On success
 * the case's parsed_metadata is updated server-side and we invalidate
 * the useCase query so the Raven page picks up the new attorneys/judge.
 */
export function useExtractMetadata() {
  const queryClient = useQueryClient();

  return useMutation<
    { attorneys: unknown[]; judge: unknown; llm_model: string },
    Error,
    { caseId: string }
  >({
    mutationFn: async ({ caseId }) => {
      const supabase = getSupabase();
      if (!supabase) throw new Error("Supabase not configured");

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not signed in");

      const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

      const res = await fetch(`${baseUrl}/functions/v1/extract-metadata`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          apikey: anonKey,
        },
        body: JSON.stringify({ case_id: caseId }),
      });

      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const parsed = JSON.parse(text);
          detail = parsed.error ?? text;
        } catch {}
        throw new Error(`Metadata extract failed (HTTP ${res.status}): ${detail}`);
      }

      return JSON.parse(text);
    },
    onSuccess: (_data, vars) => {
      // Invalidate the case query so parsed_metadata refreshes in the UI.
      queryClient.invalidateQueries({ queryKey: ["case", vars.caseId] });
    },
  });
}
